import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownLeft, faArrowUpLeft } from "@fortawesome/pro-solid-svg-icons";

import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { StatusCode } from "api/protocols";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { LocaleCurrency, LocaleCurrencyString } from "helpers/Locale";
import { capitalize, getCreditCardIcon, valueToString } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { TABLET_WIDTH } from "helpers/ScreenSizes";

import { Select } from "components/select/";
import { Badge } from "components/badge/Badge";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Sheet from "components/sheet/Sheet";
// import AccountScheduledPaymentModal from "./AccountScheduledPaymentModal";
// import EditAccountScheduledPaymentModal from "./EditAccountScheduledPaymentModal";
import DataTable from "../../../../../../components/dataTable/DataTable";
// import HouseAccountPaymentModal from "./HouseAccountPaymentModal";
// import NewCustomerHouseAccountModal from "./NewCustomerHouseAccountModal";
// import HouseAccountAdjustModal from "./HouseAccountAdjustModal";
// import { EditCustomerHouseAccountModal, EditHouseAccountState } from "./EditCustomerHouseAccountModal";
// import {
//   HouseAccountStatementType,
//   HouseAccountTransactionType,
//   HouseAccountType,
//   TransactionKind,
// } from "../../context/contextTypes";

// import "./CustomerHouseAccounts.scss";
import Input from "components/form/input/Input";
import { useGuestPortalContext } from "pages/guest/Portal/GuestPortalContext";
// import { useCustomerContext } from "../../context/CustomerContext";
import {
  HouseAccountScheduledPayment,
  HouseAccountStatementType,
  HouseAccountTransactionType,
  HouseAccountType,
  TransactionKind,
} from "../../context/contextTypes";
import {
  DownloadCustomerAccountStatements,
  GetAccountScheduledPayments,
  GetAccountTransactions,
  GetCustomerHouseAccounts,
  GetCustomerStatements,
  PostAccountTransaction,
  PutAccountTransactionCapture,
} from "api/rpc/2024-04/customer/customer";
import { ButtonNew } from "components/buttonNew";
import {
  GetCustomerPaymentMethods,
  ICustomerPaymentMethod,
  PostPaymentMethod,
  PostSetup,
} from "api/rpc/2024-04/customer/paymentMethod";
import "./portalHouseAccounts.scss";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import { DBFilterContext } from "redux/reducers/filters/filters";
import { filterContextIsEqual } from "components/accordion/Accordion";
import { faCreditCard, faHouse, faPlus, faQuestionCircle, faUser, faWallet } from "@fortawesome/pro-regular-svg-icons";
import CardSection from "components/cardSection/CardSection";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import CustomerFilterAccordion from "components/accordion/filterAccordions/CustomerFilterAccordion";
import { GetTeeOnPaymentAccounts } from "api/rpc/2024-04/customer/payment/teeonPayments/accounts";
import {
  HouseAccountType as FacilityHouseAccountType,
  TMinimumSpend,
} from "pages/secure/facility/customer/context/contextTypes";
import ProgressCircle from "components/progressCircle/ProgressCircle";
import Card from "components/card/Card";
import AccountCustomersCell from "pages/secure/facility/settings/accounts/AccountCustomersCell";
import { ICustomer } from "redux/reducers/models/customer";

const OVERVIEW_VISIBLE_CUSTOMER_NAMES = 3;

function getAccountCustomers(account: HouseAccountType | null | undefined): ICustomer[] {
  if (!account) {
    return [];
  }
  if (account.customers && account.customers.length > 0) {
    return account.customers;
  }
  if (account.customer) {
    return [account.customer];
  }
  return [];
}

function getOverviewAccountCustomers(
  account: HouseAccountType | null | undefined,
  viewedCustomerId?: number | null,
) {
  const customers = getAccountCustomers(account);
  if (!viewedCustomerId || customers.length <= 1) {
    return customers;
  }

  const viewedCustomer = customers.find(customer => customer.id === viewedCustomerId);
  if (!viewedCustomer) {
    return customers;
  }

  return [viewedCustomer, ...customers.filter(customer => customer.id !== viewedCustomerId)];
}

interface ILoadHouseAccountsProps {
  customerId: number;
  /** Display overlay-spinner while loading */
  globalLoader: boolean;
  /** Selected Account after loading HouseAccounts */
  selectedAccountIndex: number;
  token?: CancelToken;
}

interface IHouseAccount {
  // HouseAccounts Tab
  houseAccounts: HouseAccountType[];
  selectedHouseAccount: HouseAccountType;
  accountScheduledPayments: Array<HouseAccountScheduledPayment>;
  accountTransactions: HouseAccountTransactionType[];
  paymentAmount: string;
  accountStatements: HouseAccountStatementType[];
  paymentMethods: Array<ICustomerPaymentMethod>;
  selectedPaymentMethod: ICustomerPaymentMethod;
  dateRangeStart: Date;
  dateRangeEnd: Date;
}

export default function PortalHouseAccounts() {
  const { t } = useTranslation();
  const { portalState } = useGuestPortalContext();
  const stripe = useStripe();
  const today = new Date();

  const [activeFilters, setActiveFilters] = useState<DBFilterContext[]>(undefined);
  const filters = useAppSelector(store => store.billingPageFilters);
  const { facilityStore } = useAppSelector(store => store);
  const elements = useElements();

  const [teeOnPaymentActivated, setTeeOnPaymentActivated] = useState<boolean>(false);

  const [houseAccountState, updateHouseAccountState] = useState<IHouseAccount>({
    houseAccounts: null,
    selectedHouseAccount: null,
    paymentAmount: null,
    accountStatements: [],
    accountTransactions: [],
    accountScheduledPayments: [],
    paymentMethods: null,
    selectedPaymentMethod: null,
    dateRangeStart: today,
    dateRangeEnd: today,
  });

  const [paymentModalState, setPaymentModalState] = useState({
    pageNumber: 0,
    payFull: true,
    payAmount: false,
    clientSecret: "",
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    addNewCardVisible: false,
    saveCard: false,
    newCardAdded: false,
  });

  const [dateFilter, setDateFilter] = useState({
    start_date: "",
    end_date: "",
  });

  const guest = portalState.guest;
  const { Option } = Select;
  const windowSize = useWindowSize();

  const dispatch = useAppDispatch();

  const [paymentModalVisible, setPaymentModalVisible] = useState(false);

  const [selectedTab, setSelectedTab] = useState<number>(0);

  const sortedAccountScheduledPayments = [...(houseAccountState.accountScheduledPayments ?? [])].sort(
    sortScheduledPaymentsByLatest,
  );

  const tableTabs = [
    {
      id: "statements",
      content: t("Statements"),
    },
    {
      id: "transactions",
      content: t("Transactions"),
    },
    {
      id: "scheduledPayments",
      content: t("Schedule payments"),
    },
  ];

  // load all HouseAccounts associated with customer
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (houseAccountState.houseAccounts === null) {
      void loadHouseAccounts({
        customerId: guest.id,
        selectedAccountIndex: 0,
        globalLoader: true,
        token: source.token,
      });
    }

    return () => {
      source.cancel("CustomerHouseAccounts unmounted.");
    };
  }, []);

  useEffect(() => {
    if (activeFilters === undefined) {
      setActiveFilters(filters);
    }
  }, [activeFilters]);

  // Update statements & transactions as side-effect of changing selected HouseAccount
  useEffect(() => {
    const source = axios.CancelToken.source();
    setTeeOnPaymentActivated(false);

    if (houseAccountState.selectedHouseAccount) {
      void loadCustomerStatements(source.token);
      void loadAccountTransactions(source.token);
      void loadScheduledPayments(source.token);
      void loadPaymentMethods(source.token);
      void loadTeeOnPayments(houseAccountState.selectedHouseAccount?.facility_id, source.token);
    } else {
      updateHouseAccountState(prevState => ({
        ...prevState,
        accountStatements: [],
        accountScheduledPayments: [],
        accountTransactions: [],
      }));
    }

    return () => {
      source.cancel("CustomerHouseAccounts unmounted.");
    };
  }, [houseAccountState.selectedHouseAccount]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    switch (selectedTab) {
      case 0:
        void loadCustomerStatements(source.token);

        break;
      case 1:
        void loadAccountTransactions(source.token);

        break;
      case 2:
        void loadScheduledPayments(source.token);

        break;

      default:
        break;
    }
  }, [activeFilters, dateFilter]);

  async function loadHouseAccounts(args: ILoadHouseAccountsProps) {
    const { customerId, globalLoader, token } = args;

    const accountRes = await GetCustomerHouseAccounts({ customer_id: customerId, closed: false }, globalLoader, token);
    if (Number(accountRes.status) !== Number(StatusCode.OK)) {
      return;
    }

    updateHouseAccountState(prevState => ({
      ...prevState,
      houseAccounts: accountRes.data,
      selectedHouseAccount: accountRes.data.length > 0 ? accountRes.data[0] : [],
      paymentAmount: accountRes.data.length > 0 ? accountRes.data[0].balance : "",
    }));


    void loadTeeOnPayments(Number(accountRes?.data[0]?.facility_id), token);
  }

  async function loadPaymentMethods(cancelToken?: CancelToken) {
    const paymentMethodRes = await GetCustomerPaymentMethods({ type: ["card"] }, false, cancelToken);

    if (paymentMethodRes?.status !== StatusCode.OK) {
      if (paymentMethodRes.message === "Cancelled") {
        return;
      }
      dispatch(showError("Error getting payment methods"));
      updateHouseAccountState(prevState => ({ ...prevState, paymentMethods: [] }));
      return;
    }
    updateHouseAccountState(prevState => ({ ...prevState, paymentMethods: paymentMethodRes?.data?.card }));
  }

  async function loadCustomerStatements(token?: CancelToken) {
    if (houseAccountState.accountStatements !== null) {
      updateHouseAccountState(prevState => ({
        ...prevState,
        accountStatements: null,
      }));
    }

    // remove unused filters from accordion
    let filterParams = activeFilters
      ?.filter(grouping => grouping.value.length !== 0)
      .map(ele => {
        return {
          label: ele.label,
          value: ele.value,
        };
      });

    // apply date filter if given
    if (dateFilter.start_date.length !== 0) {
      filterParams = filterParams.concat([
        { label: "start_date", value: [dateFilter.start_date] },
        { label: "end_date", value: [dateFilter.end_date] },
      ]);
    }

    const params = {
      filters: filterParams?.length > 0 ? filterParams : undefined,
      account_id: houseAccountState.selectedHouseAccount?.id,
    };

    const statementRes = await GetCustomerStatements(params, false, token);
    if (token && token.reason) {
      return;
    }
    if (statementRes.status !== StatusCode.OK) {
      return;
    }

    updateHouseAccountState(prevState => ({
      ...prevState,
      accountStatements: statementRes.data ? statementRes.data : [],
    }));
  }

  async function loadAccountTransactions(token?: CancelToken) {
    if (houseAccountState.accountStatements !== null) {
      updateHouseAccountState(prevState => ({
        ...prevState,
        accountTransactions: null,
      }));
    }

    // remove unused filters from accordion
    let filterParams = activeFilters
      ?.filter(grouping => grouping.value.length !== 0)
      .map(ele => {
        return {
          label: ele.label,
          value: ele.value,
        };
      });

    // apply date filter if given
    if (dateFilter.start_date.length !== 0) {
      filterParams = filterParams.concat([
        { label: "start_date", value: [dateFilter.start_date] },
        { label: "end_date", value: [dateFilter.end_date] },
      ]);
    }

    const params = {
      filters: filterParams?.length > 0 ? filterParams : undefined,
      account_id: houseAccountState.selectedHouseAccount?.id,
    };

    const accountTransactionsRes = await GetAccountTransactions(params, false, token);

    if (accountTransactionsRes.status !== StatusCode.OK) {
      if (token.reason) {
        console.log(token.reason.message);
      } else {
        dispatch(showError(t("Error getting account transactions")));
      }
      return;
    }

    const markRefunds = determineRefundedTransactions(accountTransactionsRes.data as HouseAccountTransactionType[]);

    const facilities = facilityStore.facilities;

    for (let i = 0; i < markRefunds.length; i++) {
      for (let k = 0; k < facilities.length; k++) {
        if (markRefunds[i].facility_id === facilities[k].id) {
          markRefunds[i].facility = facilities[k];
        }
      }
    }

    updateHouseAccountState(prevState => ({
      ...prevState,
      accountTransactions: markRefunds,
    }));
  }

  async function loadScheduledPayments(cancelToken?: CancelToken) {
    if (houseAccountState.accountScheduledPayments !== null) {
      updateHouseAccountState(prevState => ({
        ...prevState,
        accountScheduledPayments: null,
      }));
    }

    // remove unused filters from accordion
    let filterParams = activeFilters
      ?.filter(grouping => grouping.value.length !== 0)
      .map(ele => {
        return {
          label: ele.label,
          value: ele.value,
        };
      });

    // apply date filter if given
    if (dateFilter.start_date.length !== 0) {
      filterParams = filterParams.concat([
        { label: "start_date", value: [dateFilter.start_date] },
        { label: "end_date", value: [dateFilter.end_date] },
      ]);
    }

    const params = {
      filters: filterParams?.length > 0 ? filterParams : undefined,
      account_id: houseAccountState.selectedHouseAccount?.id,
    };

    const paymentRes = await GetAccountScheduledPayments(params, false, cancelToken);

    if (cancelToken && cancelToken.reason) {
      return;
    }
    if (paymentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting scheduled payments")));
      return;
    }

    updateHouseAccountState(prevState => ({
      ...prevState,
      accountScheduledPayments: paymentRes?.data ? paymentRes?.data : [],
    }));
  }

  async function loadTeeOnPayments(facility_id: number, cancelToken?: CancelToken) {
    const params = {
      facility_id: facility_id,
    };

    const teeOnPaymentRes = await GetTeeOnPaymentAccounts(params, true, cancelToken);

    if (cancelToken && cancelToken.reason) {
      return;
    }

    if (teeOnPaymentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting Tee On Payments")));
    }

    setTeeOnPaymentActivated(teeOnPaymentRes.data?.activated);
  }

  async function downloadAccountStatement(statement_id: number, statementWindow: Window | null) {
    if (!statementWindow) {
      dispatch(showError(t("Please allow pop-ups to view your statement.")));
      return;
    }

    const downloadRes = await DownloadCustomerAccountStatements({ account_statement_id: statement_id }, true);
    if (downloadRes.status !== StatusCode.OK) {
      statementWindow.close();
      dispatch(showError(String(downloadRes.data.message)));
      return;
    }

    statementWindow.document.open();
    statementWindow.document.write(String(downloadRes.data));
    statementWindow.document.close();
  }

  function handleHouseAccountChange(accountId: number) {
    if (accountId !== null) {
      const account = houseAccountState.houseAccounts.find(account => {
        return account.id === accountId;
      });

      updateHouseAccountState(prevState => ({
        ...prevState,
        selectedHouseAccount: account,
        paymentAmount: account?.balance.toString(),
      }));
    }
  }

  function handlePaymentMethodChange(method: ICustomerPaymentMethod) {
    updateHouseAccountState(prevState => ({ ...prevState, selectedPaymentMethod: method }));
  }

  function handleClosePaymentModal() {
    setPaymentModalVisible(false);
    updateHouseAccountState(prevState => ({
      ...prevState,
      paymentAmount: String(houseAccountState?.selectedHouseAccount?.balance),
      selectedPaymentMethod: null,
    }));
    setPaymentModalState(prevState => ({ ...prevState, payFull: true, payAmount: false }));
  }

  function setDateRangeStart(date: Date) {
    setDateFilter(prevState => ({ ...prevState, start_date: date.toISOString().split("T")[0] }));

    updateHouseAccountState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setDateFilter(prevState => ({ ...prevState, end_date: date.toISOString().split("T")[0] }));

    updateHouseAccountState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  async function handleProcessCustomerCard() {
    const transactionRes = await PostAccountTransaction(
      {
        kind: "account_payment",
        amount: Number(houseAccountState.paymentAmount),
        account_id: houseAccountState?.selectedHouseAccount?.id,
        payment_option_id: houseAccountState?.selectedPaymentMethod?.id,
        // note: paymentState.note,
        facility_id: houseAccountState?.selectedHouseAccount?.facility_id,
      },
      true,
    );

    if (Number(transactionRes.status) === Number(StatusCode.OK)) {
      updateHouseAccountState(prevState => ({
        ...prevState,
        transaction: transactionRes.data,
        clientSecret: transactionRes.data.client_secret,
      }));
    } else {
      dispatch(showError(t("Error creating transaction")));
      dispatch(dequeue());
      return;
    }

    if (!stripe) {
      console.log("Stripe setup error");
      return;
    }

    let stripePaymentMethod;

    if (paymentModalState.newCardAdded) {
      stripePaymentMethod = houseAccountState.selectedPaymentMethod;
    } else {
      stripePaymentMethod = houseAccountState.paymentMethods.find(
        method => method.id === houseAccountState.selectedPaymentMethod.id,
      );
    }

    dispatch(enqueue());
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
      String(transactionRes.data.client_secret),
      {
        payment_method: stripePaymentMethod.stripe_payment_method_id,
      },
    );

    if (stripeError) {
      dispatch(showError(stripeError.message));
      dispatch(dequeue());
      return;
    }

    //Capture the payment
    const captureRes = await PutAccountTransactionCapture(
      { payment_intent_id: paymentIntent.id, transaction_id: transactionRes.data?.id },
      false,
    );

    if (captureRes.status !== StatusCode.OK) {
      dispatch(showError(String(captureRes.message)));
      dispatch(dequeue());
      return;
    }

    dispatch(showSuccess(t("Payment completed successfully")));
    void handleClosePaymentModal();
    setPaymentModalState(prevState => ({
      ...prevState,
      pageNumber: 0,
      payAmount: false,
      payFull: true,
      clientSecret: "",
      addNewCardVisible: false,
      newCardAdded: false,
      elementComplete: {
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
      },
    }));

    dispatch(dequeue());
    void loadHouseAccounts({
      customerId: guest.id,
      selectedAccountIndex: 0,
      globalLoader: true,
    });
  }

  function handleCheckBoxChange() {
    if (!paymentModalState.payFull) {
      updateHouseAccountState(prevState => ({
        ...prevState,
        paymentAmount: String(houseAccountState?.selectedHouseAccount?.balance),
      }));
    }

    setPaymentModalState(prevState => ({
      ...prevState,
      payFull: !paymentModalState.payFull,
      payAmount: !paymentModalState.payAmount,
    }));
  }

  async function handleAddNewCard() {
    if (!stripe || !elements) {
      dispatch(showError("Error with payment method setup"));
      return;
    }
    dispatch(enqueue());
    const stripePaymentMethodRes = await stripe.createPaymentMethod({
      type: "card",
      card: elements.getElement("cardNumber"),
    });

    if (stripePaymentMethodRes.paymentMethod) {
      const setupRes = await PostSetup(true);

      if (setupRes.status !== StatusCode.OK) {
        dispatch(showError("Error setting up payment method"));
      }

      if (stripePaymentMethodRes?.paymentMethod?.id !== "undefined" && setupRes.status === StatusCode.OK) {
        const stripeConfirmCardRes = await stripe.confirmCardSetup(String(setupRes.data.setup_intent.client_secret), {
          payment_method: stripePaymentMethodRes.paymentMethod.id,
        });
        if (stripeConfirmCardRes.error) {
          dispatch(showError(stripeConfirmCardRes.error.message));
        } else {
          const postPaymentRes = await PostPaymentMethod(
            {
              payment_method_id: stripeConfirmCardRes.setupIntent.payment_method,
              save_card: paymentModalState.saveCard,
            },
            true,
          );

          if (postPaymentRes.status !== StatusCode.OK) {
            dispatch(showError("Error creating payment method"));
          } else {
            dispatch(showSuccess("Successfully added new card"));

            setPaymentModalState(prevState => ({
              ...prevState,
              addNewCardVisible: false,
              pageNumber: 2,
              newCardAdded: true,
            }));
            updateHouseAccountState(prevState => ({ ...prevState, selectedPaymentMethod: postPaymentRes.data }));
            void loadPaymentMethods();
          }
        }
      }
    } else {
      dispatch(showError(stripePaymentMethodRes.error.message));
    }

    dispatch(dequeue());
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setPaymentModalState(prevState => ({
      ...prevState,
      elementComplete: { ...paymentModalState.elementComplete, [e.elementType]: e.complete },
    }));
  }

  function handleTabChange(index: number) {
    void setSelectedTab(index);
    setActiveFilters([]);
    setDateFilter(prevState => ({ ...prevState, end_date: "", start_date: "" }));
    updateHouseAccountState(prevState => ({ ...prevState, dateRangeStart: today, dateRangeEnd: today }));
  }

  function getStartOfPeriod(minimum: TMinimumSpend) {
      switch(minimum?.structure) {
        case "monthly":
          return moment().startOf('month');
        case "yearly":
          return moment().startOf('year');
        case "quarterly":
          return moment().startOf('quarter');
      }
    }
    function getEndOfPeriod(minimum: TMinimumSpend) {
      switch(minimum?.structure) {
        case "monthly":
          return moment().endOf('month');
        case "yearly":
          return moment().endOf('year');
        case "quarterly":
          return moment().endOf('quarter');
      }
    }
  
    function daysUntilEndOfPeriod(minimum: TMinimumSpend): number {
      switch(minimum?.structure) {
        case "monthly":
          return moment().endOf('month').diff(moment(), 'days');
        case "yearly":
          return moment().endOf('year').diff(moment(), 'days');
        case "quarterly":
          return moment().endOf('quarter').diff(moment(), 'days');
      }
    }
  
    function minimumStateDisplay(minimum: TMinimumSpend) {
    
        const spendPercent = minimum.pivot?.balance/minimum?.amount;
        const dayPercent = 1 - Number(daysUntilEndOfPeriod(minimum))/Number(getEndOfPeriod(minimum)?.diff(getStartOfPeriod(minimum), 'days'));
        
        if (spendPercent >= 1) {
          // good state
          return "complete";
        } else if (spendPercent < dayPercent && Number(daysUntilEndOfPeriod(minimum)) < 5) {
          // warning state
          return "warning";
        } else {
          // on track state
          return "on_track";
        }
      }

  return (
    <div>
      <>
        <div className="portal-ha-overview">
          <div className="portal-ha-overview__header">
            <h2 className="portal-ha-overview__title">{t("Account overview")}</h2>
            <div className="portal-ha-overview__actions">
              {teeOnPaymentActivated ? (
                <ButtonNew
                  type="primary"
                  size="medium"
                  className="portal-ha-overview__make-payment"
                  onClick={() => setPaymentModalVisible(true)}
                >
                  {t("Make payment")}
                </ButtonNew>
              ) : null}
              <Select
                className="portal-ha-overview__account-select"
                defaultValue={houseAccountState.selectedHouseAccount?.id}
                onChange={(id: number) => handleHouseAccountChange(id)}
                disabled={
                  houseAccountState.accountStatements === null || houseAccountState.accountTransactions === null
                }
                leftIcon={<FontAwesomeIcon icon={faHouse} />}
              >
                {houseAccountState.houseAccounts?.map(account => {
                  return (
                    <Option key={account.id} value={account.id} name={account.title}>
                      {account?.closed ? (
                        <span>
                          {account.title}
                          &nbsp;
                          <Badge type="gray">{t("Closed")}</Badge>
                        </span>
                      ) : (
                        <span>{account.title}</span>
                      )}
                    </Option>
                  );
                })}
              </Select>
            </div>
          </div>

          <div className="portal-ha-overview__divider" aria-hidden />

          <div className="portal-ha-overview__metrics">
            <div className="portal-ha-overview__metric">
              <div className="portal-ha-overview__metric-icon">
                <FontAwesomeIcon size="lg" icon={faWallet} />
              </div>
              <div className="portal-ha-overview__metric-copy">
                <div className="portal-ha-overview__metric-value">
                  <LocaleCurrency amount={houseAccountState.selectedHouseAccount?.balance ?? 0} />
                </div>
                <div className="portal-ha-overview__metric-label">{t("Current balance")}</div>
              </div>
            </div>

            <div className="portal-ha-overview__metric-divider" aria-hidden />

            <div className="portal-ha-overview__metric">
              <div className="portal-ha-overview__metric-icon">
                <FontAwesomeIcon size="lg" icon={faCreditCard} />
              </div>
              <div className="portal-ha-overview__metric-copy">
                <div className="portal-ha-overview__metric-value">
                  <LocaleCurrency amount={houseAccountState.selectedHouseAccount?.limit ?? 0} />
                </div>
                <div className="portal-ha-overview__metric-label">{t("Credit limit")}</div>
              </div>
            </div>

            {houseAccountState.selectedHouseAccount?.minimum ? (
              <>
                <div className="portal-ha-overview__metric-divider" aria-hidden />
                <div className="portal-ha-overview__metric portal-ha-overview__metric--minimum">
                  <div className="portal-ha-overview__progress-circle">
                    <ProgressCircle
                      progress={Math.round(
                        (houseAccountState.selectedHouseAccount.minimum.pivot?.balance /
                          Number(houseAccountState.selectedHouseAccount.minimum?.amount)) *
                          100,
                      )}
                      size={64}
                      strokeWidth={6}
                    />
                  </div>
                  <div className="portal-ha-overview__metric-copy">
                    <div className="portal-ha-overview__minimum-title">
                      {houseAccountState.selectedHouseAccount.minimum?.title || t("Minimum spending")}
                    </div>
                    <div className="portal-ha-overview__minimum-description">
                      <LocaleCurrency amount={houseAccountState.selectedHouseAccount.minimum.pivot?.balance} />
                      {t(" spent of ")}
                      <LocaleCurrency amount={houseAccountState.selectedHouseAccount.minimum?.amount} />
                    </div>
                    <div
                      className={`portal-ha-overview__minimum-days ${minimumStateDisplay(
                        houseAccountState.selectedHouseAccount.minimum,
                      )}`}
                    >
                      {daysUntilEndOfPeriod(houseAccountState.selectedHouseAccount.minimum)}
                      {t(" days remaining")}
                    </div>
                  </div>
                </div>
              </>
            ) : null}

            {getAccountCustomers(houseAccountState.selectedHouseAccount).length > 1 ? (
              <>
                <div className="portal-ha-overview__metric-divider" aria-hidden />
                <div className="portal-ha-overview__metric portal-ha-overview__metric--customers">
                  <div className="portal-ha-overview__metric-icon">
                    <FontAwesomeIcon size="lg" icon={faUser} />
                  </div>
                  <div className="portal-ha-overview__metric-copy">
                    <div className="portal-ha-overview__customers-title">{t("Customer(s)")}</div>
                    <AccountCustomersCell
                      account={
                        {
                          ...houseAccountState.selectedHouseAccount,
                          customers: getOverviewAccountCustomers(
                            houseAccountState.selectedHouseAccount,
                            guest?.id,
                          ),
                        } as FacilityHouseAccountType
                      }
                      visibleCount={OVERVIEW_VISIBLE_CUSTOMER_NAMES}
                    />
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div>
          <Card>
            <Card.Section>
            <div className="portal-ha-tab-search-container">
            <div className="portal-ha-tab-container">
              {tableTabs?.map((tab: any, index: number) => {
                return (
                  <button
                    key={index}
                    type="button"
                    className={classNames("portal-ha-tab", {
                      "portal-ha-tab_first": index === 0,
                      "portal-ha-tab_last": index === 2,
                      "portal-ha-tab_active": index === selectedTab,
                    })}
                    onClick={() => handleTabChange(index)}
                  >
                    {tab.content}
                  </button>
                );
              })}
            </div>

            <div className="portal-ha-filters-container">
              <div className="w-full">
                <DatePickerInput
                  months={windowSize.width > TABLET_WIDTH ? 2 : 1}
                  position="right"
                  startingDate={houseAccountState.dateRangeStart}
                  setStartingDate={setDateRangeStart}
                  endingDate={houseAccountState.dateRangeEnd}
                  setEndingDate={setDateRangeEnd}
                  showQuickOptions={windowSize.width > TABLET_WIDTH}
                />
              </div>
              <div>
                <CustomerFilterAccordion
                  activeFilters={activeFilters}
                  applyAllAction={updatedFilters => {
                    setActiveFilters(updatedFilters);
                  }}
                  clearAllAction={updatedFilters => {
                    if (updatedFilters.length === 0) {
                      return undefined;
                    }
                    setActiveFilters(updatedFilters);
                  }}
                  allFiltersAreApplied={filterContextIsEqual(activeFilters, filters)}
                  rightAligned
                  isLoading={activeFilters === undefined}
                  paymentStatus={selectedTab === 2}
                  paymentType={null}
                  priceMatch
                  creditCard={selectedTab === 1}
                />
              </div>
            </div>
          </div>
          <div style={{ display: selectedTab === 0 ? "block" : "none", paddingTop: "8px" }}>
            <DataTable<HouseAccountStatementType>
              columns={[
                { label: t("Statement") },
                { label: t("Amount") },
                { label: t("Start Date") },
                { label: t("End Date") },
                { label: "" },
              ]}
              loading={!houseAccountState.accountStatements}
            >
              {houseAccountState.accountStatements?.map(statement => {
                return (
                  <tr key={statement.id}>
                    <td>{statement.id}</td>
                    <td>
                      <LocaleCurrency currency={"CAD"} amount={statement.current_balance} />
                    </td>
                    <td>{moment(statement.start_date).utc().format("LL")}</td>
                    <td>{moment(statement.end_date).utc().format("LL")}</td>

                    <td className="text-right cursor-pointer">
                      <p
                        onClick={() => {
                          const statementWindow = window.open("about:blank", `house-account-statement-${statement.id}`);
                          void downloadAccountStatement(statement.id, statementWindow);
                        }}
                      >
                        {t("Download")}
                      </p>
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          </div>

          <div style={{ display: selectedTab === 1 ? "block" : "none", paddingTop: "8px" }}>
            <DataTable<HouseAccountTransactionType>
              columns={[
                { label: t("Date") },
                { label: t("Currency") },
                { label: t("Amount") },
                { label: t("Account") },
                { label: t("Payment Method") },
              ]}
              loading={!houseAccountState.accountTransactions}
            >
              {houseAccountState.accountTransactions?.map((transaction, index: number) => {
                return (
                  <tr key={index} id={String(transaction.id)}>
                    <td>{moment(transaction?.processed_at).format("ll")}</td>

                    <td> {transaction?.currency.toUpperCase()}</td>

                    <td>
                      <span>
                        <LocaleCurrency amount={transaction?.amount} currency={transaction?.currency} />{" "}
                      </span>
                    </td>
                    <td>
                      <p style={{ fontSize: "12px" }}>
                        {valueToString(transaction?.kind)}{" "}
                        <span className="ml-2">{transactionBadge(transaction?.kind, t)} </span>
                      </p>
                    </td>
                    <td>
                      <span>{valueToString(transaction?.payment_type) || ""}</span>
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          </div>

          <div style={{ display: selectedTab === 2 ? "block" : "none", paddingTop: "8px" }}>
            <DataTable
              columns={[
                { label: t("Date") },
                // { label: t("Currency") },
                { label: t("Amount") },
                { label: t("Status") },
                { label: t("Processed On") },
                { label: t("Note"), width: "40%" },
              ]}
              loading={!houseAccountState?.accountScheduledPayments}
            >
              {sortedAccountScheduledPayments.map((payment, index: number) => {
                return (
                  <tr key={index}>
                    <td>{moment(payment?.process_date).format("ll")}</td>
                    <td>
                      <div className="house-accounts-scheduled-payment-amount">
                        <LocaleCurrency amount={payment?.amount} />
                        {payment?.charge_balance ? (
                          <p className="house-accounts-scheduled-payment-amount-secondary">
                            {t("Full statement balance")}
                          </p>
                        ) : null}
                      </div>
                    </td>
                    <td>{scheduledPaymentBadge(payment, t)}</td>
                    <td>{payment?.processed_date ? moment(payment.processed_date).format("ll") : ""}</td>
                    <td>{renderScheduledPaymentNote(payment, t)}</td>
                  </tr>
                );
              })}
            </DataTable>
          </div>
          </Card.Section>
        </Card>
        </div>
      </>

      {/**
       *
       *  MODALS AND POPUPS
       *
       */}

      {houseAccountState.selectedHouseAccount && paymentModalVisible && (
        <Sheet
          open={paymentModalVisible}
          size="small"
          height="flexible"
          closable
          title={
            paymentModalState.pageNumber === 0
              ? t("Pay account balance (1/3)")
              : paymentModalState.pageNumber === 1
              ? "Choose payment method (2/3)"
              : paymentModalState.pageNumber === 2
              ? "Review payment (3/3)"
              : ""
          }
          onCancel={() =>
            paymentModalState.pageNumber === 2
              ? setPaymentModalState(prevState => ({ ...prevState, pageNumber: 1 }))
              : paymentModalState.pageNumber === 1
              ? paymentModalState.addNewCardVisible
                ? setPaymentModalState(prevState => ({ ...prevState, addNewCardVisible: false }))
                : setPaymentModalState(prevState => ({ ...prevState, pageNumber: 0 }))
              : paymentModalState.pageNumber === 0
              ? handleClosePaymentModal()
              : null
          }
          onOk={() =>
            paymentModalState.pageNumber === 0
              ? setPaymentModalState(prevState => ({ ...prevState, pageNumber: 1 }))
              : paymentModalState.pageNumber === 1
              ? paymentModalState.addNewCardVisible
                ? void handleAddNewCard()
                : setPaymentModalState(prevState => ({ ...prevState, pageNumber: 2 }))
              : paymentModalState.pageNumber === 2
              ? handleProcessCustomerCard()
              : null
          }
          cancelText={
            paymentModalState.pageNumber === 2
              ? "Back"
              : paymentModalState.pageNumber === 1
              ? paymentModalState.addNewCardVisible
                ? "Cancel"
                : "Back"
              : paymentModalState.pageNumber === 0
              ? "Cancel"
              : null
          }
          okText={
            paymentModalState.pageNumber === 2
              ? "Process Payment"
              : paymentModalState.pageNumber === 1
              ? "Next"
              : paymentModalState.pageNumber === 0
              ? "Next"
              : null
          }
          backDropCancel={false}
          overflow
          okDisabled={
            paymentModalState.payAmount ? (Number(houseAccountState.paymentAmount) <= 0 ? true : false) : false
          }
          // okLoading={paymentInProgress}
        >
          {paymentModalState.pageNumber === 0 && (
            <div className="">
              <div className="mt-2 mb-2">
                <p>{"How much would you like to pay on your account?"}</p>
              </div>

              <div className="mt-2 mb-2">
                <div className="mt-3 mb-2">
                  <Checkbox
                    size="small"
                    value={paymentModalState.payFull}
                    checked={paymentModalState.payFull}
                    onChange={handleCheckBoxChange}
                    label={"Pay full balance " + "($" + houseAccountState.selectedHouseAccount?.balance + ")"}
                    circle
                  />
                </div>

                <div className="mt-3 mb-4">
                  <Checkbox
                    size="small"
                    value={paymentModalState.payAmount}
                    checked={paymentModalState.payAmount}
                    onChange={handleCheckBoxChange}
                    label={t("Pay other amount")}
                    circle
                  />
                </div>
              </div>

              <div className="mt-2">
                <Input
                  prefix="$"
                  type="number"
                  label={t("Amount")}
                  id="paymentAmount"
                  value={houseAccountState.paymentAmount}
                  onChange={event =>
                    updateHouseAccountState(prevState => ({ ...prevState, paymentAmount: event.target.value }))
                  }
                  disabled={paymentModalState.payFull}
                />
              </div>
            </div>
          )}

          {paymentModalState.pageNumber === 1 &&
            (!paymentModalState.addNewCardVisible ? (
              <div>
                <div>
                  <p>{"Select a payment method to charge " + "($" + houseAccountState.paymentAmount + ")"}</p>
                </div>

                <div className="portal-ha-credit-cards-container">
                  {houseAccountState.paymentMethods?.map(method => {
                    return (
                      <div
                        key={method.id}
                        onClick={() => handlePaymentMethodChange(method)}
                        // className="portal-ha-credit-card-container"
                        className={classNames("portal-ha-credit-card-container", {
                          "portal-ha-credit-card-container_selected":
                            method?.id === houseAccountState?.selectedPaymentMethod?.id,
                        })}
                      >
                        <div>
                          <FontAwesomeIcon size="2x" icon={getCreditCardIcon(method.brand)} />
                        </div>

                        <div>
                          <p className="portal-ha-credit-card-container_card_text">
                            {capitalize(method.brand)} ending in {method.last4}
                          </p>

                          <p className="portal-ha-credit-card-container_expiry_text">
                            {"Expiry "}
                            {method.exp_month ? method.exp_month + "/" + method.exp_year : method.exp_year}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  className="cursor-pointer"
                  onClick={() => setPaymentModalState(prevState => ({ ...prevState, addNewCardVisible: true }))}
                >
                  <p>
                    <span>
                      <FontAwesomeIcon icon={faPlus} />
                    </span>
                    {" Add new payment method"}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <p>{"Enter your credit card details below."}</p>

                <div className="mt-2 mb-2">
                  <CardSection onChange={handleCardSectionChange} type="two-row" />
                </div>

                <div>
                  <Checkbox
                    size="small"
                    value={paymentModalState.saveCard}
                    checked={paymentModalState.saveCard}
                    onChange={() =>
                      setPaymentModalState(prevState => ({ ...prevState, saveCard: !paymentModalState.saveCard }))
                    }
                    label={t("Save card for future bookings")}
                    circle
                  />
                </div>
              </div>
            ))}

          {paymentModalState.pageNumber === 2 && (
            <div>
              <div className="mt-3 mb-4">
                <p className="portal-ha-payment-info-text">
                  {"You're paying ("}
                  <span className="portal-ha-payment-info-value">${houseAccountState.paymentAmount}</span>
                  {") towards your house account balance"}
                </p>
              </div>

              <div
                style={{ cursor: "default" }}
                className={classNames("portal-ha-credit-card-container mt-2 mb-2", {})}
              >
                <div>
                  <FontAwesomeIcon
                    size="2x"
                    icon={getCreditCardIcon(houseAccountState?.selectedPaymentMethod?.brand)}
                  />
                </div>

                <div>
                  <p className="portal-ha-credit-card-container_card_text">
                    {capitalize(houseAccountState?.selectedPaymentMethod?.brand)} ending in{" "}
                    {houseAccountState?.selectedPaymentMethod?.last4}
                  </p>

                  <p className="portal-ha-credit-card-container_expiry_text">
                    {"Expiry "}
                    {houseAccountState.selectedPaymentMethod?.exp_month
                      ? houseAccountState.selectedPaymentMethod?.exp_month +
                        "/" +
                        houseAccountState.selectedPaymentMethod?.exp_year
                      : houseAccountState.selectedPaymentMethod?.exp_year}
                  </p>
                </div>
              </div>

              <div className="mt-4 mb-3">
                <span className="portal-ha-payment-process-note">{"Note:"}</span>{" "}
                <span className="portal-ha-payment-process-text">
                  {"Your payment will be processed after you confirm"}
                </span>
              </div>
            </div>
          )}
        </Sheet>
      )}
    </div>
  );
}

/** Refunded transaction based off parent_id of the transaction == the currentTransactionId */
function determineRefundedTransactions(transactions: HouseAccountTransactionType[]): HouseAccountTransactionType[] {
  const filteredTransactions = transactions.filter(
    (transaction: Record<string, any>) => transaction?.kind !== "authorization",
  );
  const refundedTransactions = transactions.filter(
    (transaction: Record<string, any>) => transaction?.parent_id && transaction?.kind === "account_refund",
  );

  const markRefunds = [...filteredTransactions]?.reduce(
    (markedRefunds, currentTransaction, index) => {
      const refunded = refundedTransactions?.some(
        (transaction: Record<string, any>) => transaction?.parent_id === currentTransaction?.id,
      );

      // Adding in 'refunded' param to HouseAccountTransactionType
      if (refunded) {
        markedRefunds[index] = { ...markedRefunds[index], refunded: true };
      } else {
        markedRefunds[index] = { ...markedRefunds[index], refunded: false };
      }
      return markedRefunds;
    },
    [...filteredTransactions],
  );

  return markRefunds;
}

// Account Payments and Refunds decrease balance on account
export const transactionBadge = (type: TransactionKind, t: any) => {
  switch (type) {
    case "account_payment":
    case "refund":
    case "credit_adjustment":
      return (
        <Badge type="success" iconLeft={<FontAwesomeIcon icon={faArrowUpLeft} />}>
          {t('Credit')}
        </Badge>
      );
    case "debit_adjustment":
    case "sale":
      return (
        <Badge type="error" iconLeft={<FontAwesomeIcon icon={faArrowDownLeft} />}>
          {t('Debit')}
        </Badge>
      );
    default:
      return <></>;
  }
};

const sortScheduledPaymentsByLatest = (
  a: HouseAccountScheduledPayment,
  b: HouseAccountScheduledPayment,
): number => {
  const dateDiff = moment(b.process_date).valueOf() - moment(a.process_date).valueOf();
  if (dateDiff !== 0) {
    return dateDiff;
  }

  return (b.id ?? 0) - (a.id ?? 0);
};

const formatScheduledPaymentErrorMessage = (
  message?: string | null,
  errorCode?: string | null,
): string | null => {
  if (errorCode === "insufficient_funds" || /insufficient\s*funds/i.test(message ?? "")) {
    return "Insufficient funds";
  }

  return message || null;
};

const formatDeclinedScheduledPaymentNote = (
  payment: HouseAccountScheduledPayment,
  t: (key: string, options?: Record<string, string>) => string,
): string => {
  const reason = formatScheduledPaymentErrorMessage(payment.error_message, payment.error_code);
  const amount = LocaleCurrencyString({ amount: payment.amount });
  const date = moment(payment.process_date).format("ll");
  const nextAttemptDate = payment.next_attempt_date
    ? moment(payment.next_attempt_date).format("ll")
    : null;

  let content = reason
    ? t("The scheduled payment of {{amount}} on {{date}} has been declined due to {{reason}}", {
        amount,
        date,
        reason,
      })
    : t("The scheduled payment of {{amount}} on {{date}} has been declined.", {
        amount,
        date,
      });

  if (nextAttemptDate) {
    content = `${content} ${t("It will be retried on {{nextAttemptDate}}.", {
      nextAttemptDate,
    })}`;
  }

  return content;
};

const renderScheduledPaymentNote = (
  payment: HouseAccountScheduledPayment,
  t: (key: string, options?: Record<string, string>) => string,
) => {
  const existingNote = payment.note?.trim() ? payment.note : null;
  const declinedNote =
    payment.status === "failed" ? formatDeclinedScheduledPaymentNote(payment, t) : null;

  if (!existingNote && !declinedNote) {
    return null;
  }

  return (
    <div className="house-accounts-scheduled-payment-note">
      {existingNote ? <p className="house-accounts-scheduled-payment-note-text">{existingNote}</p> : null}
      {existingNote && declinedNote ? <hr className="house-accounts-scheduled-payment-note-divider" /> : null}
      {declinedNote ? <p className="house-accounts-scheduled-payment-note-text">{declinedNote}</p> : null}
    </div>
  );
};

const scheduledPaymentBadge = (payment: HouseAccountScheduledPayment, t: (key: string) => string) => {
  switch (payment.status) {
    case "complete":
      return <Badge type="success">{capitalize(payment.status)}</Badge>;
    case "canceled":
      return <Badge type="error">{capitalize(payment.status)}</Badge>;
    case "failed": {
      const declineReason = formatScheduledPaymentErrorMessage(payment.error_message, payment.error_code);
      return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <Badge type="error">{t("Declined")}</Badge>
          {declineReason ? (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id={`scheduled-payment-declined-tooltip-${payment.id}`}>{declineReason}</Tooltip>}
            >
              <span style={{ display: "inline-flex", cursor: "pointer", color: "#98A2B3" }}>
                <FontAwesomeIcon icon={faQuestionCircle} />
              </span>
            </OverlayTrigger>
          ) : null}
        </span>
      );
    }
    default:
      return <Badge type="gray">{capitalize(payment.status)}</Badge>;
  }
};
